/**
 * Price tables — list, create, simulate, deactivate.
 *
 * Two decisions shape this screen:
 *  · Money is edited in reais but stored in integer cents. The conversion
 *    happens once, here, so no float ever reaches the API.
 *  · A saved table is never edited. Saving a new one closes the previous
 *    validity window, because an order issued in March must not be repriced by
 *    a change made in April.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'

import { api, type Categoria, type Item, type Lista, type RegraPreco, type TabelaPreco } from '../api/cliente'
import {
  Aviso,
  Botao,
  Cabecalho,
  Campo,
  Carregando,
  Cartao,
  Etiqueta,
  Modal,
  Selecao,
  Tabela,
  Vazio,
  formatarBRL,
  formatarDataCurta,
} from '../componentes/ui'

const MODOS = [
  { valor: 'cm_coluna', texto: 'Por centímetro de coluna' },
  { valor: 'lauda', texto: 'Por lauda' },
  { valor: 'linha', texto: 'Por linha' },
  { valor: 'caractere', texto: 'Por caractere' },
  { valor: 'palavra', texto: 'Por palavra' },
  { valor: 'tabela_faixa', texto: 'Por faixa de quantidade' },
]

const ROTULO_MODO: Record<string, string> = {
  cm_coluna: 'cm/coluna',
  lauda: 'lauda',
  linha: 'linha',
  caractere: 'caractere',
  palavra: 'palavra',
  tabela_faixa: 'faixa',
}

/** Reais typed by a human ("12,50") into integer cents. */
function paraCentavos(texto: string): number {
  const limpo = String(texto ?? '').replace(/\./g, '').replace(',', '.')
  const n = Number.parseFloat(limpo)
  return Number.isFinite(n) ? Math.round(n * 100) : 0
}

interface ResultadoSimulacao {
  valorTotalCentavos: number
  valorFormatado: string
  quantidadeCobrada: number
  memoria: Array<{ descricao: string; valorCentavos: number }>
}

export function Precos() {
  const qc = useQueryClient()
  const [modalAberto, setModalAberto] = useState(false)
  /** Tabela em edição. Null quando o modal está criando uma nova. */
  const [editando, setEditando] = useState<TabelaPreco | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  const [categoriaId, setCategoriaId] = useState('')
  const [descricao, setDescricao] = useState('')
  const [atoAprovacao, setAtoAprovacao] = useState('')
  const [vigenciaInicio, setVigenciaInicio] = useState(() => new Date().toISOString().slice(0, 10))
  const [modo, setModo] = useState('cm_coluna')
  const [valorUnitario, setValorUnitario] = useState('')
  const [quantidadeMinima, setQuantidadeMinima] = useState('')
  const [valorMinimo, setValorMinimo] = useState('')
  const [percentualTributos, setPercentualTributos] = useState('')

  // A EGBA cobra por CENTÍMETRO DE COLUNA: a quantidade é altura × colunas.
  // Pedir os dois separadamente evita que o operador faça a conta de cabeça —
  // e é exatamente como o PDF oficial apresenta a base de cálculo.
  const [alturaCm, setAlturaCm] = useState('20')
  const [colunas, setColunas] = useState('3')
  const [simulacao, setSimulacao] = useState<ResultadoSimulacao | null>(null)

  const quantidadeSimulada = String((Number(alturaCm) || 0) * (Number(colunas) || 0))

  const { data: categorias } = useQuery({
    queryKey: ['categorias'],
    queryFn: () => api.get<Lista<Categoria>>('/api/v1/admin/categorias'),
  })

  const { data, isLoading } = useQuery({
    queryKey: ['precos'],
    queryFn: () => api.get<Lista<TabelaPreco>>('/api/v1/admin/precos'),
  })

  function montarRegra(): RegraPreco {
    const regra: RegraPreco = { modo: modo as RegraPreco['modo'], moeda: 'BRL' }
    if (modo !== 'tabela_faixa') regra.valorUnitarioCentavos = paraCentavos(valorUnitario)
    if (quantidadeMinima) regra.quantidadeMinima = Number(quantidadeMinima)
    if (valorMinimo) regra.valorMinimoCentavos = paraCentavos(valorMinimo)
    if (percentualTributos) regra.percentualTributos = Number(percentualTributos)
    return regra
  }

  const simular = useMutation({
    mutationFn: () =>
      api.post<Item<ResultadoSimulacao>>('/api/v1/admin/precos/simular', {
        regraPreco: montarRegra(),
        quantidade: Number(quantidadeSimulada) || 0,
      }),
    onSuccess: (r) => {
      setSimulacao(r.dados)
      setErro(null)
    },
    onError: (e: Error) => {
      setSimulacao(null)
      setErro(e.message)
    },
  })

  const salvar = useMutation({
    mutationFn: () => {
      if (!editando) {
        return api.post<Item<TabelaPreco>>('/api/v1/admin/precos', {
          categoriaId,
          descricao,
          atoAprovacao: atoAprovacao || undefined,
          vigenciaInicio,
          regraPreco: montarRegra(),
        })
      }

      // Manda só o que MUDOU. Mandar sempre o corpo inteiro fazia a API
      // classificar toda edição como estrutural e recusar com 'tabela_em_uso'
      // qualquer tabela que já tivesse cobrado — inclusive quando o operador
      // só queria corrigir a descrição.
      const corpo: Record<string, unknown> = {}
      if (descricao !== editando.descricao) corpo.descricao = descricao
      if ((atoAprovacao || null) !== (editando.atoAprovacao ?? null)) {
        corpo.atoAprovacao = atoAprovacao || undefined
      }
      if (vigenciaInicio !== editando.vigenciaInicio) corpo.vigenciaInicio = vigenciaInicio
      if (categoriaId !== editando.categoriaId) corpo.categoriaId = categoriaId

      const regra = montarRegra()
      if (JSON.stringify(regra) !== JSON.stringify(editando.regraPreco)) {
        corpo.regraPreco = regra
      }

      return api.put<Item<TabelaPreco>>(`/api/v1/admin/precos/${editando.id}`, corpo)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['precos'] })
      fechar()
    },
    onError: (e: Error) => setErro(e.message),
  })

  const encerrarVigencia = useMutation({
    mutationFn: ({ id, ate }: { id: string; ate: string }) =>
      api.put<Item<TabelaPreco>>(`/api/v1/admin/precos/${id}`, { vigenciaFim: ate }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['precos'] }),
    onError: (e: Error) => setErro(e.message),
  })

  const alternarAtiva = useMutation({
    mutationFn: (t: TabelaPreco) =>
      t.ativa
        ? api.delete<Item<TabelaPreco>>(`/api/v1/admin/precos/${t.id}`)
        : api.post<Item<TabelaPreco>>(`/api/v1/admin/precos/${t.id}/reativar`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['precos'] }),
    onError: (e: Error) => setErro(e.message),
  })

  function abrir() {
    setEditando(null)
    setCategoriaId(categorias?.dados[0]?.id ?? '')
    setDescricao('')
    setAtoAprovacao('')
    setVigenciaInicio(new Date().toISOString().slice(0, 10))
    setModo('cm_coluna')
    setValorUnitario('')
    setQuantidadeMinima('')
    setValorMinimo('')
    setPercentualTributos('')
    setAlturaCm('20')
    setColunas('3')
    setSimulacao(null)
    setErro(null)
    setModalAberto(true)
  }

  /**
   * Opens the modal on an existing table.
   *
   * Every field is filled from the stored rule, so "edit" means correcting what
   * is there — an editor that opens blank and silently overwrites is how a
   * price loses its footnote about the approving act.
   */
  function abrirEdicao(t: TabelaPreco) {
    const r = t.regraPreco
    setEditando(t)
    setCategoriaId(t.categoriaId)
    setDescricao(t.descricao)
    setAtoAprovacao(t.atoAprovacao ?? '')
    setVigenciaInicio(t.vigenciaInicio)
    setModo(r.modo)
    setValorUnitario(
      r.valorUnitarioCentavos != null ? (r.valorUnitarioCentavos / 100).toFixed(2).replace('.', ',') : '',
    )
    setQuantidadeMinima(r.quantidadeMinima != null ? String(r.quantidadeMinima) : '')
    setValorMinimo(
      r.valorMinimoCentavos != null ? (r.valorMinimoCentavos / 100).toFixed(2).replace('.', ',') : '',
    )
    setPercentualTributos(r.percentualTributos != null ? String(r.percentualTributos) : '')
    setAlturaCm('20')
    setColunas('3')
    setSimulacao(null)
    setErro(null)
    setModalAberto(true)
  }

  function fechar() {
    setModalAberto(false)
    setEditando(null)
    setErro(null)
    setSimulacao(null)
  }

  function aoEnviar(e: FormEvent) {
    e.preventDefault()
    setErro(null)
    salvar.mutate()
  }

  const tabelas = data?.dados ?? []
  const hoje = new Date().toISOString().slice(0, 10)
  const estaVigente = (t: TabelaPreco) =>
    t.ativa && t.vigenciaInicio <= hoje && (!t.vigenciaFim || t.vigenciaFim >= hoje)

  return (
    <div className="space-y-6">
      <Aviso tom="info" titulo="A EGBA é dona destes valores">
        A plataforma não define preço. Cadastre aqui a tabela oficial vigente — o cálculo do pedido
        usa exatamente esta regra, e a memória de cálculo é mostrada ao cliente.
      </Aviso>

      <Cartao
        titulo="Tabelas de preço"
        descricao="Salvar uma nova tabela encerra a vigência da anterior automaticamente. Pedidos já emitidos mantêm o preço aplicado."
        acoes={<Botao onClick={abrir}>Nova tabela</Botao>}
      >
        {isLoading ? (
          <Carregando />
        ) : tabelas.length === 0 ? (
          <Vazio
            titulo="Nenhuma tabela cadastrada"
            descricao="Sem tabela vigente, a cobrança fica bloqueada e o pedido não avança para pagamento."
            acao={<Botao onClick={abrir}>Cadastrar tabela</Botao>}
          />
        ) : (
          <Tabela>
            <Cabecalho colunas={['Tabela', 'Categoria', 'Regra', 'Vigência', 'Situação', '']} />
            <tbody className="divide-y divide-slate-100">
              {tabelas.map((t) => (
                <tr key={t.id} className="transition hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-900">{t.descricao}</p>
                    {t.atoAprovacao && (
                      <p className="text-xs text-slate-400">{t.atoAprovacao}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{t.categoriaNome ?? '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-slate-600 tabular">
                    {t.regraPreco.valorUnitarioCentavos != null
                      ? `${formatarBRL(t.regraPreco.valorUnitarioCentavos)} / ${ROTULO_MODO[t.regraPreco.modo] ?? t.regraPreco.modo}`
                      : `${t.regraPreco.faixas?.length ?? 0} faixas`}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-slate-600 tabular">
                    {formatarDataCurta(t.vigenciaInicio)} →{' '}
                    {t.vigenciaFim ? formatarDataCurta(t.vigenciaFim) : 'indeterminada'}
                  </td>
                  <td className="px-4 py-3">
                    {!t.ativa ? (
                      <Etiqueta tom="neutro">Desativada</Etiqueta>
                    ) : estaVigente(t) ? (
                      <Etiqueta tom="sucesso">Vigente</Etiqueta>
                    ) : t.vigenciaInicio > hoje ? (
                      <Etiqueta tom="marca">Futura</Etiqueta>
                    ) : (
                      <Etiqueta tom="neutro">Encerrada</Etiqueta>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <Botao variante="secundario" tamanho="sm" onClick={() => abrirEdicao(t)}>
                        Editar
                      </Botao>
                      {t.ativa && !t.vigenciaFim && (
                        <Botao
                          variante="secundario"
                          tamanho="sm"
                          carregando={encerrarVigencia.isPending}
                          onClick={() =>
                            encerrarVigencia.mutate({ id: t.id, ate: new Date().toISOString().slice(0, 10) })
                          }
                        >
                          Encerrar hoje
                        </Botao>
                      )}
                      <Botao
                        variante={t.ativa ? 'perigo' : 'secundario'}
                        tamanho="sm"
                        carregando={alternarAtiva.isPending}
                        onClick={() => alternarAtiva.mutate(t)}
                      >
                        {t.ativa ? 'Desativar' : 'Reativar'}
                      </Botao>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Tabela>
        )}
      </Cartao>

      {/* --------------------------------------------------------- criar */}
      <Modal
        aberto={modalAberto}
        aoFechar={fechar}
        largura="xl"
        titulo={editando ? 'Editar tabela de preço' : 'Nova tabela de preço'}
        descricao="Simule antes de salvar — a memória de cálculo mostra exatamente o que o cliente verá."
      >
        {editando && (
          <div className="mb-4">
            <Aviso tom="atencao">
              Se esta tabela já precificou algum pedido, o valor e o início da vigência ficam
              travados: o pedido guarda uma cópia da regra, e reescrevê-la aqui faria a tabela
              deixar de descrever o que foi cobrado. Nesse caso, encerre a vigência e cadastre a
              próxima.
            </Aviso>
          </div>
        )}

        <form onSubmit={aoEnviar} className="grid gap-6 lg:grid-cols-[1.15fr_1fr]">
          {/* ---- Definição ---- */}
          <div className="space-y-4">
            <Selecao
              rotulo="Categoria"
              value={categoriaId}
              onChange={(e) => setCategoriaId(e.target.value)}
              opcoes={(categorias?.dados ?? []).map((c) => ({ valor: c.id, texto: c.nome }))}
            />

            <Campo
              rotulo="Descrição da tabela"
              required
              obrigatorio
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="Tabela 2026 — escolas"
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                rotulo="Início da vigência"
                type="date"
                required
                obrigatorio
                value={vigenciaInicio}
                onChange={(e) => setVigenciaInicio(e.target.value)}
              />
              <Campo
                rotulo="Ato de aprovação"
                value={atoAprovacao}
                onChange={(e) => setAtoAprovacao(e.target.value)}
                placeholder="Portaria EGBA 12/2026"
                ajuda="Portaria, resolução ou ofício."
              />
            </div>

            <Selecao
              rotulo="Forma de cobrança"
              value={modo}
              onChange={(e) => {
                // Fora de cm/coluna não existe coluna: sem zerar isto, a
                // quantidade simulada continuava sendo altura × 3 e o valor
                // saía triplicado — na tela onde se decide um preço público.
                if (e.target.value !== 'cm_coluna') setColunas('1')
                else setColunas('3')
                setModo(e.target.value)
                setSimulacao(null)
              }}
              opcoes={MODOS}
            />

            {modo !== 'tabela_faixa' && (
              <Campo
                rotulo={`Valor por ${ROTULO_MODO[modo] ?? 'unidade'} (R$)`}
                required
                obrigatorio
                inputMode="decimal"
                value={valorUnitario}
                onChange={(e) => setValorUnitario(e.target.value)}
                placeholder="12,50"
              />
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                rotulo="Quantidade mínima"
                type="number"
                min={0}
                value={quantidadeMinima}
                onChange={(e) => setQuantidadeMinima(e.target.value)}
                ajuda="Cobra pelo menos esta quantidade."
              />
              <Campo
                rotulo="Valor mínimo (R$)"
                inputMode="decimal"
                value={valorMinimo}
                onChange={(e) => setValorMinimo(e.target.value)}
                ajuda="Piso do pedido."
              />
            </div>

            <Campo
              rotulo="Tributos (%)"
              type="number"
              min={0}
              max={100}
              step="0.01"
              value={percentualTributos}
              onChange={(e) => setPercentualTributos(e.target.value)}
              ajuda="Aplicado por último, sobre o total já com descontos e acréscimos."
            />
          </div>

          {/* ---- Simulador ---- */}
          <div className="space-y-4 rounded-lg bg-slate-50 p-5">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Simulador</h3>
              <p className="mt-0.5 text-xs text-slate-500">
                Confira o efeito da regra antes de salvar.
              </p>
            </div>

            {modo === 'cm_coluna' ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Campo
                    rotulo="Altura da matéria (cm)"
                    type="number"
                    min={0}
                    step="0.5"
                    value={alturaCm}
                    onChange={(e) => setAlturaCm(e.target.value)}
                  />
                  <Selecao
                    rotulo="Largura"
                    value={colunas}
                    onChange={(e) => setColunas(e.target.value)}
                    opcoes={[
                      { valor: '3', texto: '12 cm — 3 colunas' },
                      { valor: '6', texto: '24 cm — 6 colunas' },
                    ]}
                  />
                </div>
                <p className="-mt-1 text-xs text-slate-500">
                  Cobrança = altura × colunas ={' '}
                  <strong className="text-slate-700 tabular">{quantidadeSimulada} cm/coluna</strong>.
                  Página simples = 6 colunas × 30 cm.
                </p>
              </>
            ) : (
              <Campo
                rotulo={`Quantidade (${ROTULO_MODO[modo] ?? 'unidades'})`}
                type="number"
                min={0}
                value={alturaCm}
                onChange={(e) => {
                  setAlturaCm(e.target.value)
                  setColunas('1')
                }}
              />
            )}

            <Botao
              type="button"
              variante="secundario"
              onClick={() => simular.mutate()}
              carregando={simular.isPending}
              className="w-full"
            >
              Calcular
            </Botao>

            {simulacao ? (
              <div className="surgir space-y-3 rounded-lg border border-slate-200 bg-white p-4">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-slate-500">Total</span>
                  <span className="text-2xl font-semibold text-slate-900 tabular">
                    {simulacao.valorFormatado}
                  </span>
                </div>
                <div className="space-y-1.5 border-t border-slate-100 pt-3">
                  {simulacao.memoria.map((l, i) => (
                    <div key={i} className="flex justify-between gap-4 text-xs">
                      <span className="text-slate-600">{l.descricao}</span>
                      <span
                        className={`shrink-0 tabular ${
                          l.valorCentavos < 0
                            ? 'text-[color:var(--color-sucesso)]'
                            : 'text-slate-700'
                        }`}
                      >
                        {formatarBRL(l.valorCentavos)}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-400">
                  Quantidade cobrada: {simulacao.quantidadeCobrada}
                </p>
              </div>
            ) : (
              <p className="rounded-lg border border-dashed border-slate-300 px-4 py-8 text-center text-xs text-slate-400">
                Preencha a regra e clique em Calcular.
              </p>
            )}
          </div>

          {erro && (
            <div className="lg:col-span-2">
              <Aviso tom="perigo">{erro}</Aviso>
            </div>
          )}

          <div className="flex justify-end gap-3 border-t border-slate-200 pt-4 lg:col-span-2">
            <Botao type="button" variante="secundario" onClick={fechar}>
              Cancelar
            </Botao>
            <Botao type="submit" carregando={salvar.isPending}>
              {editando ? 'Salvar alterações' : 'Salvar tabela'}
            </Botao>
          </div>
        </form>
      </Modal>
    </div>
  )
}
